import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { fetchValues } from './api'
import {
  operatorsFor,
  takesValue,
  type ColumnDescriptor,
  type FilterOperator,
  type FilterSpec,
} from './types'
import { columnLabel } from './columnLabel'
import { valueKey } from './cellValue'
import { Icon } from '@/components/Icon'

/**
 * Конструктор фильтров (ТЗ §5.1).
 *
 * Операторы предлагаются **по типу колонки**: `contains` над числом или `between` над
 * логическим значением сервер отвергнет, и пользователь получит ошибку вместо результата
 * — за то, что выбрал из предложенного ему же списка.
 *
 * Значение фильтра нигде не склеивается с запросом: оно уходит параметром, а оператор —
 * кодом из закрытого перечисления. Это свойство сервера, но интерфейс не должен создавать
 * впечатление, будто здесь можно написать выражение.
 */

interface FilterBuilderProps {
  descriptors: ColumnDescriptor[]
  filters: FilterSpec[]
  onChange: (filters: FilterSpec[]) => void
  /** Ключ каталога — для перевода кодов значений в строках условий. */
  entity?: string
}

/**
 * Содержимое поповера «Фильтр»: применённые условия строками и строка нового условия.
 *
 * Условие применяется сразу по «Добавить», без отдельной кнопки «Применить»: список под
 * поповером перечитывается на глазах, и видно, что условие сделало. Кнопка «Применить»
 * ставила бы между условием и его результатом шаг, о котором забывают, — и фильтр,
 * набранный, но не применённый, выглядел бы применённым.
 */
export function FilterBuilder({ descriptors, filters, onChange, entity }: FilterBuilderProps) {
  const { t } = useApp()
  const filterable = useMemo(
    () => descriptors.filter((column) => column.filterable),
    [descriptors],
  )

  const [draftColumn, setDraftColumn] = useState<string>('')
  const [draftOperator, setDraftOperator] = useState<FilterOperator>('contains')
  const [draftValue, setDraftValue] = useState<string>('')

  const selected = filterable.find((column) => column.name === draftColumn)
  const operators = selected ? operatorsFor(selected.type, selected.reference != null) : []

  // #94: values the column actually holds, offered as suggestions for an exact match, so a
  // status is picked rather than typed («partially_paid» is not something people remember).
  // Only for plain text columns: a reference holds keys, and a free-text column would offer a
  // thousand titles. The server reads them in the caller's scope and under field rights, so
  // the picker does not list values of rows the list itself would not show.
  const suggestible =
    entity !== undefined && selected !== undefined && selected.type === 'text' && selected.reference == null
    && (draftOperator === 'eq' || draftOperator === 'ne' || draftOperator === 'in')
  const suggestions = useQuery({
    queryKey: ['list-values', entity, selected?.name],
    enabled: suggestible,
    queryFn: ({ signal }) => fetchValues(entity!, selected!.name, signal),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const listId = `fb-values-${entity ?? ''}-${selected?.name ?? ''}`

  function add() {
    if (!selected) return
    const needsValue = takesValue(draftOperator)
    if (needsValue && draftValue.trim() === '') return

    onChange([
      ...filters,
      {
        column: selected.name,
        operator: draftOperator,
        // Значение уходит строкой: разбор в нужный тип делает сервер по каталогу, и
        // дублировать здесь его правила значит однажды разойтись с ними.
        value: needsValue ? parseValue(draftOperator, draftValue) : undefined,
      },
    ])
    setDraftValue('')
  }

  return (
    <div className="fbuilder">
      <div className="panel-h fb-head">
        <span className="h3">{t('filter.title')}</span>
        <span className="spacer" />
        <span className="t-sm muted">{t('filter.allMustMatch')}</span>
      </div>
      <div className="fb-body">
        {filters.map((filter, index) => (
          <div key={`${filter.column}-${index}`} className="fb-row">
            <span className="fb-join">{index === 0 ? t('filter.where') : t('filter.and')}</span>
            <span className="input input-sm is-readonly">{columnLabel(t, descriptors, filter.column)}</span>
            <span className="input input-sm is-readonly">{t(`filter.op.${filter.operator}`)}</span>
            <span className="input input-sm is-readonly ellipsis">
              {describeValue(filter, entity, t)}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-icon"
              aria-label={t('filter.remove')}
              onClick={() => onChange(filters.filter((_, i) => i !== index))}
            >
              <Icon name="close" size="sm" />
            </button>
          </div>
        ))}

        <div className="fb-row">
          <span className="fb-join">{filters.length === 0 ? t('filter.where') : t('filter.and')}</span>
          <select
            className="input input-sm"
            aria-label={t('filter.column')}
            value={draftColumn}
            onChange={(event) => {
              const name = event.target.value
              setDraftColumn(name)
              const column = filterable.find((candidate) => candidate.name === name)
              // Оператор сбрасывается вместе с колонкой: оставленный от прежнего типа он
              // отправился бы на сервер заведомо неприменимым.
              const allowed = column ? operatorsFor(column.type, column.reference != null) : []
              setDraftOperator(allowed[0] ?? 'eq')
              setDraftValue('')
            }}
          >
            <option value="">{t('filter.column')}</option>
            {filterable.map((column) => (
              <option key={column.name} value={column.name}>
                {columnLabel(t, descriptors, column.name)}
              </option>
            ))}
          </select>

          <select
            className="input input-sm"
            aria-label={t('filter.operator')}
            value={draftOperator}
            disabled={!selected}
            onChange={(event) => setDraftOperator(event.target.value as FilterOperator)}
          >
            {operators.map((operator) => (
              <option key={operator} value={operator}>
                {t(`filter.op.${operator}`)}
              </option>
            ))}
          </select>

          {takesValue(draftOperator) ? (
            <>
            {suggestible && suggestions.data && (
              <datalist id={listId} data-testid="filter-values">
                {suggestions.data.values.map((value) => {
                  const code = String(value ?? '')
                  const key = valueKey(entity!, selected!.name, code)
                  const word = t(key)
                  return <option key={code} value={code} label={word === key ? code : word} />
                })}
              </datalist>
            )}
            <input
              className="input input-sm"
              aria-label={t('filter.value')}
              list={suggestible && suggestions.data ? listId : undefined}
              value={draftValue}
              disabled={!selected}
              placeholder={
                draftOperator === 'between' || draftOperator === 'in'
                  ? t('filter.valueList')
                  : t('filter.value')
              }
              onChange={(event) => setDraftValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') add()
              }}
            />
            </>
          ) : (
            <span />
          )}
          <span />
        </div>
      </div>
      <div className="modal-f fb-foot">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={filters.length === 0}
          onClick={() => onChange([])}
        >
          {t('filter.clearAll')}
        </button>
        <button type="button" className="btn btn-primary btn-sm" disabled={!selected} onClick={add}>
          <Icon name="plus" size="sm" />
          {t('filter.add')}
        </button>
      </div>
    </div>
  )
}

/**
 * Разбирает введённое значение.
 *
 * Списочные операторы (`between`, `in`) ждут массив; запятая как разделитель — это
 * соглашение интерфейса, и оно объявлено в подсказке к полю, а не угадывается.
 */
function parseValue(operator: FilterOperator, raw: string): unknown {
  if (operator === 'between' || operator === 'in') {
    return raw
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part !== '')
  }
  return raw.trim()
}

/**
 * Значение условия словами: код состояния — переводом (`open` → «Открыта»), список — через
 * запятую. Тот же перевод, что в ячейке таблицы, иначе токен назвал бы значение иначе, чем
 * строка под ним.
 */
export function describeValue(
  filter: FilterSpec,
  entity: string | undefined,
  t: (key: string) => string,
): string {
  if (!takesValue(filter.operator)) return ''
  const word = (value: unknown) => {
    const text = String(value ?? '')
    if (!entity) return text
    const key = valueKey(entity, filter.column, text)
    const translated = t(key)
    return translated === key ? text : translated
  }
  if (Array.isArray(filter.value)) return filter.value.map(word).join(', ')
  return word(filter.value)
}
